import { ref, computed, onMounted } from "vue";
import { useStaticAssets } from "@/composables/useStaticAssets";

/**
 * Composable para gerenciar backgrounds aleatórios de orixás na página de login
 */
export function useRandomBackground() {
	const { getRandomBackground, getBackgroundInfo, availableBackgrounds } =
		useStaticAssets();

	// Estado reativo para o background atual
	const currentBackgroundKey = ref<string>("");
	const currentBackgroundUrl = ref<string>("");

	/**
	 * Seleciona um background aleatório
	 */
	const selectRandomBackground = () => {
		const randomIndex = Math.floor(Math.random() * availableBackgrounds.length);
		const selectedKey = availableBackgrounds[randomIndex];

		currentBackgroundKey.value = selectedKey;
		const { url } = getBackgroundInfo(selectedKey);
		currentBackgroundUrl.value = url;
	};

	/**
	 * Informações do background atual
	 */
	const backgroundInfo = computed(() => {
		if (!currentBackgroundKey.value) return null;
		return getBackgroundInfo(currentBackgroundKey.value);
	});

	/**
	 * Estilo CSS para aplicar o background
	 */
	const backgroundStyle = computed(() => {
		if (!currentBackgroundUrl.value) return {};

		return {
			backgroundImage: `url(${currentBackgroundUrl.value})`,
			backgroundSize: "cover",
			backgroundPosition: "center",
			backgroundRepeat: "no-repeat",
		};
	});

	/**
	 * Estilo CSS para overlay com opacidade
	 */
	const overlayStyle = computed(() => {
		if (!currentBackgroundUrl.value) {
			return {};
		}

		return {
			position: "absolute" as const,
			top: 0,
			left: 0,
			right: 0,
			bottom: 0,
			backgroundImage: `url(${currentBackgroundUrl.value})`,
			backgroundSize: "cover",
			backgroundPosition: "center",
			backgroundRepeat: "no-repeat",
			opacity: 0.25,
			zIndex: 1,
		};
	});

	/**
	 * Força a seleção de um background específico
	 */
	const selectBackground = (backgroundKey: string) => {
		if (availableBackgrounds.includes(backgroundKey)) {
			currentBackgroundKey.value = backgroundKey;
			const { url } = getBackgroundInfo(backgroundKey);
			currentBackgroundUrl.value = url;
		}
	};

	// Seleciona um background aleatório ao montar o composable
	onMounted(() => {
		selectRandomBackground();
	});

	return {
		currentBackgroundKey,
		currentBackgroundUrl,
		backgroundInfo,
		backgroundStyle,
		overlayStyle,
		selectRandomBackground,
		selectBackground,
		availableBackgrounds,
	};
}
